import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import styles from "./Navbar.module.css";
import logo from "../assets/logo-klein.jpg";
import ThemeToggle from "./ThemeToggle";
import { servicesNav } from "../data/servicesNav";


type Dir = "up" | "down";
function useSmartHideNav(opts?: { bottomSentinelId?: string; minDelta?: number }) {
  const bottomId = opts?.bottomSentinelId ?? "page-end-sentinel";
  const MIN_DELTA = opts?.minDelta ?? 6;

  const [hidden, setHidden] = useState(false);
  const [atTop, setAtTop] = useState(true);
  const [nearBottom, setNearBottom] = useState(false);
  const dirRef = useRef<Dir>("up");
  const lastY = useRef<number>(0);
  const ticking = useRef(false);

  useEffect(() => {
    const el = document.getElementById(bottomId);
    if (!el || !("IntersectionObserver" in window)) {
      const check = () => {
        const y = window.scrollY;
        const bottom =
          y + window.innerHeight >=
          (document.scrollingElement?.scrollHeight ||
            document.documentElement.scrollHeight) - 1;
        setNearBottom(bottom);
      };
      check();
      window.addEventListener("scroll", check, { passive: true });
      window.addEventListener("resize", check);
      return () => {
        window.removeEventListener("scroll", check);
        window.removeEventListener("resize", check);
      };
    }
    const io = new IntersectionObserver(
      (entries) => setNearBottom(entries[0]?.isIntersecting ?? false),
      { threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [bottomId]);

  useEffect(() => {
    const update = () => {
      ticking.current = false;
      const y = window.scrollY;
      const dy = y - lastY.current;

      if (Math.abs(dy) >= MIN_DELTA) {
        dirRef.current = dy > 0 ? "down" : "up";
      }
      lastY.current = y;
      setAtTop(y <= 8);

      if (dirRef.current === "down" && !nearBottom && y > 24) {
        setHidden(true);
      } else {
        setHidden(false);
      }
    };

    const onScroll = () => {
      if (!ticking.current) {
        ticking.current = true;
        requestAnimationFrame(update);
      }
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [nearBottom, MIN_DELTA]);

  return { hidden, atTop, nearBottom, dir: dirRef.current as Dir };
}

const NAV_HEIGHT = 80;
const TOPBAR_H = 4;
const MEGA_W = 640;

type MenuItem = { to: string; label: string; desc?: string };
type Section = { kicker: string; items: MenuItem[] };

const unternehmenBase: Section = {
  kicker: "Wer wir sind",
  items: [
    { to: "/", label: "Startseite" },
    { to: "/about", label: "Über uns" },
    { to: "/jobs", label: "Karriere" },
    
    { to: "/aktuelles", label: "Aktuelles & Presse" }, 
  ],
};

const dienstleistungen: Section = {
  kicker: "Unser Leistungsspektrum",
  items: [
    {
      to: "/dienstleistungen",
      label: "Alle Leistungen",
      desc: "Kompletter Überblick",
    },
    ...servicesNav.map((s) => ({
      to: `/dienstleistungen/${s.path}`,
      label: s.title,
      desc: s.tagline || "",
    })),
  ],
};

const kontaktUndRecht: Section = {
  kicker: "Kontakt & Rechtliches",
  items: [
    { to: "/contact", label: "Kontakt" },
    { to: "/impressum", label: "Impressum" },
    { to: "/datenschutz", label: "Datenschutz" },
  ],
};

const portfolio: Section = {
  kicker: "Portfolio",
  items: [{ to: "/projects", label: "Unsere Projekte" }],
};

export default function Navbar() {
  const { pathname } = useLocation();

  const isExact = (p: string) => pathname === p || pathname === `${p}/`;
  const isUnder = (p: string) =>
    pathname === p || pathname === `${p}/` || pathname.startsWith(`${p}/`);
  const isHome = isExact("/");

const unternehmenActive =
  ["/about", "/aktuelles", "/jobs"].some(isUnder) ||
  (isExact("/") && pathname === "/");

  const dienstActive = isUnder("/dienstleistungen");
  const kontaktActive = ["/contact", "/impressum", "/datenschutz", "/kontakt/erfolg"].some(isUnder);
  const projekteActive = isUnder("/projects");

  const forceAwake = !isHome;

  const [sheetOpen, setSheetOpen] = useState(false);
  const [hoverOpen, setHoverOpen] = useState(false);
  const [hoverIndex, setHoverIndex] = useState<0 | 1 | 2>(0);
  const [megaLeft, setMegaLeft] = useState(16);

  const { hidden, atTop, nearBottom, dir } = useSmartHideNav();

  const navInnerRef = useRef<HTMLDivElement | null>(null);
  const unternehmenRef = useRef<HTMLButtonElement | null>(null);
  const dienstRef = useRef<HTMLButtonElement | null>(null);
  const kontaktRef = useRef<HTMLAnchorElement | null>(null);

  const scrollYRef = useRef(0);

  const hoverTimer = useRef<number | null>(null);
  const cancelHideHover = () => {
    if (hoverTimer.current !== null) window.clearTimeout(hoverTimer.current);
    hoverTimer.current = null;
  };
  const scheduleHideHover = () => {
    cancelHideHover();
    hoverTimer.current = window.setTimeout(() => setHoverOpen(false), 280) as unknown as number;
  };
  const closeHoverNow = () => {
    cancelHideHover();
    setHoverOpen(false);
  };

  useEffect(() => {
    document.documentElement.style.setProperty("--navbar-h", `${NAV_HEIGHT}px`);
    document.documentElement.style.setProperty("--topbar-h", `${TOPBAR_H}px`);
  }, []);

  useLayoutEffect(() => {
    const rootEl = navInnerRef.current;
    if (!rootEl) return;
    const leftEl = rootEl.querySelector(`.${styles.left}`) as HTMLElement | null;
    const rightEl = rootEl.querySelector(`.${styles.right}`) as HTMLElement | null;

    const setVars = () => {
      const lw = leftEl ? leftEl.offsetWidth : 0;
      const rw = rightEl ? rightEl.offsetWidth : 0;
      rootEl.style.setProperty("--left-w", `${lw}px`);
      rootEl.style.setProperty("--right-w", `${rw}px`);
    };
    setVars();

    const ro = new ResizeObserver(setVars);
    if (leftEl) ro.observe(leftEl);
    if (rightEl) ro.observe(rightEl);
    window.addEventListener("resize", setVars, { passive: true });
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", setVars);
    };
  }, []);

  useEffect(() => {
    setSheetOpen(false);
    setHoverOpen(false);
  }, [pathname]);

  useEffect(() => {
    const lockSheet = () => {
      scrollYRef.current = window.scrollY || 0;

      document.body.classList.add("nav-open");
      document.body.style.overflow = "hidden";
      (document.body as HTMLElement).style.overscrollBehaviorY = "none";

      const html = document.documentElement;
      (html as HTMLElement).style.overscrollBehaviorY = "none";

      const root = document.getElementById("root");
      if (root) {
        root.setAttribute("inert", "");
        root.classList.add("app-inert");
      }

      const stopTouchMove = (e: TouchEvent) => {
        const el = e.target as HTMLElement;
        if (!el.closest?.(`.${styles.sheet}`)) e.preventDefault();
      };
      document.addEventListener("touchmove", stopTouchMove, { passive: false });

      (lockSheet as any)._stop = () =>
        document.removeEventListener("touchmove", stopTouchMove as any);
    };

    const unlockSheet = () => {
      document.body.classList.remove("nav-open");
      document.body.style.overflow = "";
      (document.body as HTMLElement).style.overscrollBehaviorY = "";

      const html = document.documentElement;
      (html as HTMLElement).style.overscrollBehaviorY = "";

      const root = document.getElementById("root");
      if (root) {
        root.removeAttribute("inert");
        root.classList.remove("app-inert");
      }

      (lockSheet as any)._stop?.();
    };

    if (sheetOpen) {
      lockSheet();
      return unlockSheet;
    }
    return undefined;
  }, [sheetOpen]);

  useEffect(() => {
    const reset = () => {
      document.body.classList.remove("nav-open", "nav-mega");
      document.documentElement.classList.remove("nav-open", "nav-mega");
      document.body.style.overflow = "";
      (document.body as HTMLElement).style.overscrollBehaviorY = "";
      document.documentElement.style.overflow = "";
      document.documentElement.style.overflowY = "";
      (document.documentElement as HTMLElement).style.overscrollBehaviorY = "";

      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      document.body.style.paddingRight = "";

      const root = document.getElementById("root");
      if (root) {
        root.removeAttribute("inert");
        root.classList.remove("app-inert");
      }
    };
    reset();
    return reset;
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setSheetOpen(false);
        setHoverOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (sheetOpen) setHoverOpen(false);
  }, [sheetOpen]);

  useEffect(() => {
    const enable = hoverOpen && !sheetOpen && window.innerWidth > 1000;
    const html = document.documentElement;

    if (enable) html.classList.add("nav-locked");
    else html.classList.remove("nav-locked");

    return () => html.classList.remove("nav-locked");
  }, [hoverOpen, sheetOpen]);

  useEffect(() => {
    if (!(hoverOpen && !sheetOpen && window.innerWidth > 1000)) return;

    const stopTouchMove = (e: TouchEvent) => {
      const el = e.target as HTMLElement;
      if (!el.closest?.(`.${styles.megaInner}`)) e.preventDefault();
    };

    document.addEventListener("touchmove", stopTouchMove, { passive: false });
    return () => document.removeEventListener("touchmove", stopTouchMove as any);
  }, [hoverOpen, sheetOpen]);

  const positionMegaUnder = (el: HTMLElement | null) => {
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 16;
    const preferredLeft = r.left + r.width / 2 - MEGA_W / 2;
    const left = Math.max(
      margin + window.scrollX,
      Math.min(
        preferredLeft + window.scrollX,
        window.scrollX + window.innerWidth - margin - MEGA_W
      )
    );
    setMegaLeft(left);
  };

  useEffect(() => {
    if (!hoverOpen) return;
    let raf = 0;
    const reflow = () => {
      raf = 0;
      positionMegaUnder(
        hoverIndex === 0
          ? unternehmenRef.current
          : hoverIndex === 1
          ? dienstRef.current
          : kontaktRef.current
      );
    };
    const onResizeOrScroll = () => {
      if (!raf) raf = requestAnimationFrame(reflow);
    };

    onResizeOrScroll();
    window.addEventListener("resize", onResizeOrScroll, { passive: true });
    window.addEventListener("scroll", onResizeOrScroll, { passive: true });

    return () => {
      window.removeEventListener("resize", onResizeOrScroll);
      window.removeEventListener("scroll", onResizeOrScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [hoverOpen, hoverIndex]);

  const openHover = (i: 0 | 1 | 2) => {
    if (sheetOpen || window.innerWidth <= 1000) return;
    cancelHideHover();
    setHoverIndex(i);
    positionMegaUnder(
      i === 0 ? unternehmenRef.current : i === 1 ? dienstRef.current : kontaktRef.current
    );
    setHoverOpen(true);
  };

  const colored =
    forceAwake ||
    hoverOpen ||
    sheetOpen ||
    nearBottom ||
    (!atTop && dir === "up");

  const filterStartseite = (items: MenuItem[]) =>
    isExact("/") ? items.filter((it) => !isExact(it.to)) : items;

  const unternehmen: Section = {
    ...unternehmenBase,
    items: filterStartseite(unternehmenBase.items),
  };
  const hoveredSection =
    hoverIndex === 0 ? unternehmen : hoverIndex === 1 ? dienstleistungen : kontaktUndRecht;

  useEffect(() => () => cancelHideHover(), []);

  const navigateFromSheet = () => {
    setSheetOpen(false);
  };

  const hiddenApplied = hidden && !hoverOpen && !sheetOpen;

  return (
    <>
      <header
        data-navbar
        className={[styles.navbar, colored ? styles.awake : "", hiddenApplied ? styles.hidden : ""]
          .filter(Boolean)
          .join(" ")}
        role="banner"
      >
        <div className={styles.topbar} aria-hidden="true" />
        <div
          className={styles.navInner}
          ref={navInnerRef}
          onMouseLeave={scheduleHideHover}
          onMouseEnter={cancelHideHover}
        >
          <div className={styles.left}>
            <a
              href="/"
              className={styles.logoLink}
              aria-label="Zur Startseite"
              onMouseEnter={closeHoverNow}
              onFocus={closeHoverNow}
            >
              <img src={logo} alt="AVILA" className={styles.logoImg} width={48} height={48} />
              <span className={styles.logoText}>
                <span className={styles.logoMain}>AVILA</span>
                <span className={styles.logoSub}>Projektmanagement GmbH</span>
              </span>
            </a>
          </div>

          <nav
            className={styles.center}
            aria-label="Hauptnavigation"
            onMouseLeave={scheduleHideHover}
            onMouseEnter={cancelHideHover}
          >
            <button
              ref={unternehmenRef}
              type="button"
              className={[
                styles.centerBtn,
                (hoverOpen && hoverIndex === 0) || unternehmenActive ? styles.active : "",
              ].join(" ")}
              aria-haspopup="true"
              aria-expanded={hoverOpen && hoverIndex === 0}
              onMouseEnter={() => openHover(0)}
              onFocus={() => openHover(0)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => openHover(0)}
            >
              Unternehmen
            </button>

            <NavLink
              to="/projects"
              className={({ isActive }) =>
                [styles.centerLink, isActive || projekteActive ? styles.active : ""].join(" ")
              }
              onMouseEnter={closeHoverNow}
              onFocus={closeHoverNow}
            >
              Projekte
            </NavLink>

            <button
              ref={dienstRef}
              type="button"
              className={[
                styles.centerBtn,
                (hoverOpen && hoverIndex === 1) || dienstActive ? styles.active : "",
              ].join(" ")}
              aria-haspopup="true"
              aria-expanded={hoverOpen && hoverIndex === 1}
              onMouseEnter={() => openHover(1)}
              onFocus={() => openHover(1)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => openHover(1)}
            >
              Dienstleistungen
            </button>
          </nav>

          <div className={styles.right}>
            <a
              href="/contact"
              className={[
                styles.navLink,
                (hoverOpen && hoverIndex === 2) || kontaktActive ? styles.active : "",
              ].join(" ")}
              ref={kontaktRef as React.Ref<HTMLAnchorElement>}
              aria-haspopup="true"
              aria-expanded={hoverOpen && hoverIndex === 2}
              role="button"
              onMouseEnter={() => openHover(2)}
              onFocus={() => openHover(2)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.preventDefault();
                openHover(2);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openHover(2);
                }
              }}
              onMouseLeave={scheduleHideHover}
            >
              Kontakt
            </a>

            <div className={styles.themeDesktopOnly} onMouseEnter={closeHoverNow}>
              <ThemeToggle />
            </div>

            <button
              className={[styles.menuToggle, sheetOpen ? styles.menuOpen : ""].join(" ")}
              aria-label={sheetOpen ? "Menü schließen" : "Menü öffnen"}
              aria-expanded={sheetOpen}
              aria-controls="nav-mobile"
              type="button"
              onMouseEnter={closeHoverNow}
              onClick={() => {
                closeHoverNow();
                setSheetOpen((o) => !o);
              }}
            >
              <span className={styles.menuBox} aria-hidden="true">
                <span className={styles.bar} />
                <span className={styles.bar} />
                <span className={styles.bar} />
              </span>
            </button>
          </div>
        </div>
      </header>

      {hoverOpen &&
        createPortal(
          <div className={styles.megaPortal}>
            <div
              className={styles.megaBackdrop}
              onMouseEnter={scheduleHideHover}
              onClick={closeHoverNow}
              aria-hidden="true"
            />
            <div
              className={styles.megaInner}
              style={
                {
                  ["--mega-left" as any]: `${megaLeft}px`,
                  ["--mega-w" as any]: `${MEGA_W}px`,
                } as React.CSSProperties
              }
              onMouseEnter={cancelHideHover}
              onMouseLeave={scheduleHideHover}
              role="menu"
              aria-label="Schnellnavigation"
            >
              <div className={styles.megaKicker}>{hoveredSection.kicker}</div>
              <ul className={styles.megaList}>
                {hoveredSection.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.to === "/dienstleistungen"}
                      className={({ isActive }) =>
                        isActive ? `${styles.megaItem} ${styles.active}` : styles.megaItem
                      }
                      onClick={closeHoverNow}
                    >
                      <div className={styles.megaText}>
                        <span className={styles.megaLabel}>{item.label}</span>
                        {item.desc && <small className={styles.megaDesc}>{item.desc}</small>}
                      </div>
                    
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          </div>,
          document.body
        )}

      {sheetOpen &&
        createPortal(
          <div
            className={styles.sheetOverlay}
            role="dialog"
            aria-modal="true"
            aria-label="Hauptmenü"
            id="nav-mobile"
            onClick={(e) => {
              if (e.target === e.currentTarget) setSheetOpen(false);
            }}
          >
            <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
              <div className={styles.sheetHead}>
                <img src={logo} alt="AVILA" className={styles.sheetLogo} />
                <button
                  className={styles.sheetClose}
                  aria-label="Menü schließen"
                  onClick={() => setSheetOpen(false)}
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>

              <div className={styles.sheetBody}>
                {[unternehmen, portfolio, dienstleistungen, kontaktUndRecht].map((section) => (
                  <section key={section.kicker} className={styles.sheetSection}>
                    <h4 className={styles.sheetKicker}>{section.kicker}</h4>
                    <ul className={styles.sheetList}>
                      {filterStartseite(section.items).map((item) => (
                        <li key={item.to}>
                          <NavLink
                            to={item.to}
                            end={item.to === "/dienstleistungen"}
                            className={({ isActive }) =>
                              isActive ? `${styles.sheetLink} ${styles.active}` : styles.sheetLink
                            }
                            onClick={navigateFromSheet}
                          >
                            <span>{item.label}</span>
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>

              <div className={styles.sheetFooter}>
                <div className={styles.themeInSheet}>
                  <ThemeToggle mobile />
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
